import { useState, type ReactNode } from 'react';
import { Filter } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useGetAllPorts } from '~/api/logistics';
import { Button } from '~/components/ui/buttons/Button';
import { FilterSidebar } from '~/components/ui/FilterSidebar';
import { Input } from '~/components/ui/inputs/Input';
import { SearchableSelect } from '~/components/ui/inputs/SearchableSelect';
import {
  Table,
  type TableColumn,
  type TableRow,
  useTable,
} from '~/components/ui/table';
import { PageLoadingSpinner } from '~/components/ui/spinners/PageLoadingSpinner';
import type { PortType } from '~/graphql/graphql';

const PORT_TYPE_OPTIONS: PortType[] = ['SEAPORT', 'CY', 'RAMP', 'DOOR'];

function ActiveBadge({
  active,
  label,
}: {
  active: boolean;
  label: string;
}): ReactNode {
  return (
    <span
      className={`text-xs font-medium px-2 py-1 rounded-lg whitespace-nowrap ${
        active ? 'text-success-700 bg-success-100' : 'text-neut-500 bg-neut-100'
      }`}
    >
      {label}
    </span>
  );
}

export function PortsTable() {
  const { t } = useTranslation('logistics');

  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [filterType, setFilterType] = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [filterActive, setFilterActive] = useState('true');
  const [pendingType, setPendingType] = useState('');
  const [pendingCountry, setPendingCountry] = useState('');
  const [pendingActive, setPendingActive] = useState('true');

  const { data, isLoading } = useGetAllPorts();
  const allPorts = data?.ports ?? [];

  const filtered = allPorts.filter((p) => {
    if (filterType && p.type !== filterType) return false;
    if (
      filterCountry &&
      !p.country?.toLowerCase().includes(filterCountry.toLowerCase())
    )
      return false;
    if (filterActive !== '' && String(p.active) !== filterActive) return false;
    return true;
  });

  const activeFilterCount = [
    filterType !== '',
    filterCountry !== '',
    filterActive !== 'true',
  ].filter(Boolean).length;

  const tableControls = useTable({});

  const applyFilter = () => {
    setFilterType(pendingType);
    setFilterCountry(pendingCountry);
    setFilterActive(pendingActive);
    setFilterPanelOpen(false);
  };

  const clearFilter = () => {
    setPendingType('');
    setPendingCountry('');
    setPendingActive('true');
    setFilterType('');
    setFilterCountry('');
    setFilterActive('true');
  };

  const columns: TableColumn[] = [
    { key: 'code', header: t('logistics.ports.table.code'), width: 6 },
    { key: 'city', header: t('logistics.ports.table.city'), width: 8 },
    { key: 'country', header: t('logistics.ports.table.country'), width: 8 },
    { key: 'type', header: t('logistics.ports.table.type'), width: 6 },
    { key: 'active', header: t('logistics.ports.table.active'), width: 5 },
  ];

  const rows: TableRow[] = filtered.map((port) => ({
    id: { value: port.id },
    code: { value: port.code ?? '—' },
    city: { value: port.city ?? '—' },
    country: { value: port.country ?? '—' },
    type: { value: t(`logistics.ports.type.${port.type}`) },
    active: {
      value: (
        <ActiveBadge
          active={port.active}
          label={t(`logistics.add_on_costs.active.${port.active}`)}
        />
      ),
    },
  }));

  return (
    <div className="flex gap-4">
      <div className="flex-1 bg-neut-bg-alt rounded-lg border border-neut-border shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-neut-border flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-neut-prim">
              {t('logistics.ports.title')}
            </h2>
            <p className="text-xs text-neut-sec mt-1">
              {t('logistics.ports.subtitle')}
            </p>
          </div>
          <Button
            size="sm"
            width="auto"
            onClick={() => setFilterPanelOpen((o) => !o)}
          >
            <Filter size={16} className="mr-1" />
            {t('logistics.common.filter')}
            {activeFilterCount > 0 && (
              <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 ml-1 text-xs font-semibold rounded-full bg-prim text-white">
                {activeFilterCount}
              </span>
            )}
          </Button>
        </div>

        <div className="overflow-x-auto">
          <Table
            minWidth={45}
            rows={rows}
            columns={columns}
            tableControls={tableControls}
            emptyMessage={t('logistics.ports.empty')}
          />
          {isLoading && <PageLoadingSpinner name="ports" />}
        </div>
      </div>

      {filterPanelOpen && (
        <FilterSidebar
          title={t('logistics.common.filter')}
          onClose={() => setFilterPanelOpen(false)}
          onApply={applyFilter}
          onClear={clearFilter}
        >
          <SearchableSelect
            label={t('logistics.ports.table.type')}
            name="type"
            value={pendingType}
            onChange={(_name, value) => setPendingType(value)}
            options={[
              { value: '', label: '—' },
              ...PORT_TYPE_OPTIONS.map((o) => ({
                value: o,
                label: t(`logistics.ports.type.${o}`),
              })),
            ]}
          />

          <Input
            label={t('logistics.ports.table.country')}
            name="country"
            value={pendingCountry}
            onChange={(_name, value) => setPendingCountry(value)}
          />

          <SearchableSelect
            label={t('logistics.ports.table.active')}
            name="active"
            value={pendingActive}
            onChange={(_name, value) => setPendingActive(value)}
            options={[
              { value: '', label: '—' },
              {
                value: 'true',
                label: t('logistics.add_on_costs.active.true'),
              },
              {
                value: 'false',
                label: t('logistics.add_on_costs.active.false'),
              },
            ]}
          />
        </FilterSidebar>
      )}
    </div>
  );
}
